import './ServiceStatusStyles.css';
import BlockUi from '@availity/block-ui';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { connect } from 'react-redux';
import { Button, Form, Header, Icon, Label, Message, MessageList, Popup, Statistic, Step } from 'semantic-ui-react';
import { AgencyStateType } from '../../actions/actionTypes';
import { getAgencyAlertsFeed, getAgencyVehiclesFeed, getServicePageData, getTaipReports } from '../../actions/servicePageActions';
import { AppState } from '../../reducers';
import { getSelectedOrDefaultAgency } from '../../selectors';
import { BaseServiceData, RtCoverage, ServiceStatusModel } from '../../types/servicePageTypes';
import Utils from '../../utilities/utils';
import { TaipReportsDef, TaipReportsHeader, toReportRow, UnmatchedTripsReportDef, UnmatchedTripsReportHeader } from './csvReports';


interface Props {
    agency: AgencyStateType | undefined;
}

interface StatusFeedItemParameter {
    label: string;
    value: Nullable<number>;
    alternativeValue?: string;
    isValidFunc: (value: Nullable<number>) => boolean;
    formattedValue?: string;
    description?: string;
    fileName?: string;
    downloadFileFunc?: (agencyId: string) => Promise<Blob>;
    csvData?: object[];
    sectionName?: string
}

const styles = {
    calculatedValuesDescription: {
        fontSize: '12px',
        color: '#6D6D6D',
    } as React.CSSProperties,
    statusFeedItemLabel: {
        fontSize: '12px',
        fontWeight: 400,
        textTransform: 'none',
        textAlign: 'left',
    } as React.CSSProperties,
    alternativeValues: {
        color: 'rgb(219, 40, 40)',
    } as React.CSSProperties,
    messageContainer: {
        paddingLeft: 0,
        paddingRight: 0,
    } as React.CSSProperties,
    statusMessageList: {
        display: 'flex',
        gap: '1rem',
    } as React.CSSProperties,
    warningMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
    } as React.CSSProperties,
};

const ServiceStatusForm: React.FC<Props> = ({ agency }) => {
    const [formBlockingState, setFormBlockingState] = useState(false);
    const [dataState, setDataState] = useState<ServiceStatusModel | undefined>(undefined);
    const [duplicateTaipsCsvState, setDuplicateTaipsCsvState] = useState<object[]>([]);
    const [invalidIpTaipsCsvState, setInvalidIpTaipsCsvState] = useState<object[]>([]);
    const [duplicateTaipsLoadingState, setDuplicateTaipsLoadingState] = useState(false);
    const [invalidIpLoadingState, setInvalidIpLoadingState] = useState(false);
    const duplicateTaipsCsvLinkRef = useRef<any>(null);
    const invalidIpTaipsCsvLinkRef = useRef<any>(null);

    const coverageThreshold = dataState?.coverageThreshold ?? 0;
    const latencyThreshold = dataState?.latencyThreshold ?? 0;

    useEffect(() => {
        (async () => await retrieveServiceData())();
    }, [agency?.id]);

    const retrieveServiceData = async () => {
        if (agency && agency.id) {
            setFormBlockingState(true);
            try {
                const servicePageData = await getServicePageData(agency.id);
                setDataState(servicePageData);
            } catch {
                setDataState(undefined);
            } finally {
                setFormBlockingState(false);
            }
        } else {
            setDataState(undefined);
        }
    };

    const handleDuplicateTaipsCsvClick = async () => {
        await downloadTaipsCsv('Duplicate', setDuplicateTaipsCsvState, setDuplicateTaipsLoadingState, duplicateTaipsCsvLinkRef);
    };

    const handleInvalidIpCsvClick = async () => {
        await downloadTaipsCsv('InvalidIp', setInvalidIpTaipsCsvState, setInvalidIpLoadingState, invalidIpTaipsCsvLinkRef);
    };

    const downloadTaipsCsv = async (problemType: string,
        setState: React.Dispatch<React.SetStateAction<object[]>>,
        setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
        csvLinkRef: React.MutableRefObject<any>
    ) => {
        if (!agency?.id) return;

        setIsLoading(true);
        try {
            const taips = await getTaipReports(problemType);
            const csvData = taips.map(t => toReportRow(TaipReportsDef, t));
            setState(csvData);
            setTimeout(() => {
                csvLinkRef.current?.link.click();
            }, 0);
        } catch (err) {
            setState([]);
        } finally {
            setIsLoading(false);
        }
    };

    const downloadFeed = async (fileName: string, downloadFileFunc: (agencyId: string) => Promise<Blob>) => {
        if (agency && agency.id) {
            const fileResult = await downloadFileFunc(agency.id);
            fileName = `${fileName}_${moment(new Date()).format('YYYY_MM_DD_h_mm_A')}.json`;
            const url = window.URL.createObjectURL(fileResult);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        }
    };
    const isStepCompleted = ({ vehiclesCoverage, tripsCoverage, vehiclesLatency, tripsLatency }: BaseServiceData) => {
        return Boolean(
            (vehiclesCoverage?.coverageValue === null || vehiclesCoverage?.coverageValue > coverageThreshold)
            && (tripsCoverage?.coverageValue === null || tripsCoverage?.coverageValue > coverageThreshold)
            && (vehiclesLatency === null || vehiclesLatency <= latencyThreshold)
            && (tripsLatency === null || tripsLatency <= latencyThreshold));
    };
    const isVehiclesStepCompleted = ({ vehiclesCoverage, vehiclesLatency }: BaseServiceData) => {
        return Boolean(
            (vehiclesCoverage?.coverageValue === null || vehiclesCoverage?.coverageValue > coverageThreshold)
            && (vehiclesLatency === null || vehiclesLatency <= latencyThreshold));
    };
    const isValidNumber = (value: Nullable<number>) => {
        return Boolean(value !== null);
    };
    const isValidCoverage = (coverage: Nullable<number>) => {
        return Boolean(coverage === null || coverage >= coverageThreshold);
    };
    const isValidLatency = (latency: Nullable<number>) => {
        return Boolean(latency === null || latency <= latencyThreshold);
    };
    const getStepNotificationIcon = () => {
        return <Icon name="exclamation triangle" color="red" />;
    };


    function getLatencyItemForAgencyFeed(latency: Nullable<number>, actualVehiclesLatency: Nullable<number>, description?: string) {
        let alternativeValue = '-';
        if (!latency && actualVehiclesLatency) {
            alternativeValue = actualVehiclesLatency >= 3600
                ? Utils.convertSecondsToHours({ seconds: actualVehiclesLatency })
                : actualVehiclesLatency !== null && actualVehiclesLatency > 0 ? Utils.convertSecondsToMinutes(actualVehiclesLatency) : '-';
        }
        return getLatencyItem(latency, description, alternativeValue);
    }
    function getLatencyItem(latency: Nullable<number>, description?: string, alternativeValue?: string) {
        const formattedValue = latency !== null && latency >= 3600
            ? Utils.convertSecondsToHours({ seconds: latency })
            : latency !== null && latency > 0 ? Utils.convertSecondsToMinutes(latency) : '-';
        const label = latency !== null && latency >= 3600 ? 'Latency (hours:min:sec)' : 'Latency (min:sec)';
        return getStatusFeedItem({
            label,
            value: latency,
            alternativeValue,
            formattedValue,
            isValidFunc: isValidLatency,
            description,
        });
    }

    function getCoverageItem({ coverageValue, unmatchedTrips }: RtCoverage, sectionName: string, description?: string) {
        const formattedValue = coverageValue !== null ? `${coverageValue} %` : '-';
        const csvData = unmatchedTrips.map(t => toReportRow(UnmatchedTripsReportDef, t));
        return getStatusFeedItem({
            label: 'Coverage',
            value: coverageValue,
            formattedValue,
            isValidFunc: isValidCoverage,
            csvData,
            sectionName,
            description,
        });
    }

    function getStatusFeedItem({ value, alternativeValue, formattedValue, label, isValidFunc, description, downloadFileFunc, fileName, csvData, sectionName }: StatusFeedItemParameter) {
        return <>
            <Statistic>
                <Statistic.Value>
                    {isValidFunc(value)
                        ? <Icon name="check circle" color="green" />
                        : <Icon name="exclamation circle" color="red" />
                    }
                    {formattedValue ?? (value == null ? '-' : value)} {!value && alternativeValue !== undefined && <>/<span style={styles.alternativeValues}>{alternativeValue}</span></>}
                </Statistic.Value>
                <Statistic.Label>
                    {label}
                    {downloadFileFunc &&
                        <Popup content="Download Feed" trigger={
                            <Label as="a" onClick={() => { downloadFeed(fileName ?? 'feedData', downloadFileFunc); }} basic pointing="left" color="grey">
                                <Icon name="download" />
                            </Label>}
                        />}
                    {csvData &&
                        <Popup content="Download Missing Trips" trigger={
                            <CSVLink
                                headers={UnmatchedTripsReportHeader}
                                data={csvData}
                                filename={`${sectionName ?? ''}_MissingTrips_${moment(new Date()).format('YYYY_MM_DD_h_mm_A')}.csv`}
                                target="_blank"
                                className="ui grey left pointing basic label"
                            >
                                <Icon name="download" />
                            </CSVLink>
                        }
                        />}
                    {description && <div style={styles.statusFeedItemLabel}>{description}</div>}
                </Statistic.Label>
            </Statistic>
        </>;
    }

    return (
        <BlockUi tag="div" blocking={formBlockingState}>
            <Form>
                <Header as="h1" className="reportHeader">
                    Service Status
                </Header>
                {dataState &&
                    <>
                        <Form.Group>
                            <Form.Field width={16} style={styles.messageContainer}>
                                <Message info>
                                    <Message.Header>Service Information (local time: {moment.parseZone(dataState.serviceStatusLocalTime).format('YYYY-MM-DD h:mm A')})</Message.Header>
                                    <MessageList items={dataState.responseMessages} style={styles.statusMessageList} />
                                </Message>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={16} style={styles.messageContainer}>
                                <Message warning style={styles.warningMessage}>
                                    <Message.Header>TAIP issues for the last 24 hours</Message.Header>
                                    <ul className="list" style={styles.statusMessageList}>
                                        <li className="content">
                                            <span>
                                                {dataState.taipDuplicatesCount ?? '?'} duplicates
                                                <Popup
                                                    content="Download duplicate TAIP messages"
                                                    trigger={
                                                        <Button
                                                            basic
                                                            color="grey"
                                                            className="download-csv ui grey left pointing basic label"
                                                            onClick={handleDuplicateTaipsCsvClick}
                                                            loading={duplicateTaipsLoadingState}
                                                        >
                                                            <Icon name="download" />
                                                        </Button>
                                                    }
                                                />

                                                <CSVLink
                                                    headers={TaipReportsHeader}
                                                    data={duplicateTaipsCsvState}
                                                    filename={`duplicate_taips_${moment(new Date()).format('YYYY_MM_DD_h_mm_A')}.csv`}
                                                    className="hidden"
                                                    target="_blank"
                                                    ref={duplicateTaipsCsvLinkRef}
                                                />
                                            </span>
                                        </li>
                                        <li className="content">
                                            <span>
                                                {dataState.taipInvalidIpCount ?? '?'} blocked
                                                <Popup content="Download TAIP messages with invalid IP addresses" trigger={
                                                    <Button
                                                        basic
                                                        color="grey"
                                                        className="download-csv ui grey left pointing basic label"
                                                        onClick={handleInvalidIpCsvClick}
                                                        loading={invalidIpLoadingState}
                                                    >
                                                        <Icon name="download" />
                                                    </Button>
                                                }
                                                />

                                                <CSVLink
                                                    headers={TaipReportsHeader}
                                                    data={invalidIpTaipsCsvState}
                                                    filename={`invalid_ipaddresses_taips_${moment(new Date()).format('YYYY_MM_DD_h_mm_A')}.csv`}
                                                    target="_blank"
                                                    className="hidden"
                                                    ref={invalidIpTaipsCsvLinkRef}
                                                />
                                            </span>
                                        </li>
                                    </ul>
                                </Message>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Step.Group widths={3}>
                                <Step completed={isVehiclesStepCompleted(dataState.agencyData)}>
                                    {getStepNotificationIcon()}
                                    <Step.Content>
                                        <Step.Title>Trip Assignment Feed</Step.Title>
                                        {dataState.agencyData.lastRecordLocalTime &&
                                            <Step.Description>Last record time: {moment.parseZone(dataState.agencyData.lastRecordLocalTime).format('YYYY-MM-DD h:mm A')}</Step.Description>
                                        }
                                    </Step.Content>
                                </Step>
                                <Step completed={isVehiclesStepCompleted(dataState.redisData)}>
                                    {getStepNotificationIcon()}
                                    <Step.Content>
                                        <Step.Title>Redis Cache</Step.Title>
                                    </Step.Content>
                                </Step>
                                <Step completed={isStepCompleted(dataState.transitIqData)}>
                                    {getStepNotificationIcon()}
                                    <Step.Content>
                                        <Step.Title>Transit IQ</Step.Title>
                                    </Step.Content>
                                </Step>
                            </Step.Group>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getCoverageItem(dataState.agencyData.vehiclesCoverage, 'TripAssignmentFeed')}
                                </Statistic.Group>
                            </Form.Field>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getCoverageItem(dataState.redisData.vehiclesCoverage, 'Redis')}
                                </Statistic.Group>
                            </Form.Field>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getCoverageItem(dataState.transitIqData.vehiclesCoverage, 'TransitIQ')}
                                </Statistic.Group>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={16}>
                                <Message attached="top" info>
                                    <p>"Coverage" is a portion of active trips according to the schedule which have related data in GTFS-RT feed.</p>
                                </Message>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getLatencyItemForAgencyFeed(dataState.agencyData.vehiclesLatency, dataState.agencyData.actualVehiclesLatency)}
                                </Statistic.Group>
                            </Form.Field>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getLatencyItem(dataState.redisData.vehiclesLatency)}
                                </Statistic.Group>
                            </Form.Field>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getLatencyItem(dataState.transitIqData.vehiclesLatency)}
                                </Statistic.Group>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={16}>
                                <Message attached="top" info>
                                    <p>"Latency" shows the average delay between the vehicle position time and the current time.</p>
                                </Message>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getStatusFeedItem({
                                        label: 'Vehicle Positions',
                                        value: dataState.agencyData.vehiclesCount,
                                        alternativeValue: dataState.agencyData.actualVehiclesCount ? String(dataState.agencyData.actualVehiclesCount) : '-',
                                        isValidFunc: isValidNumber,
                                        fileName: 'vehicles',
                                        downloadFileFunc: getAgencyVehiclesFeed,
                                    })}
                                </Statistic.Group>
                            </Form.Field>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getStatusFeedItem({
                                        label: 'Vehicle Positions',
                                        value: dataState.redisData.vehiclesCount,
                                        isValidFunc: isValidNumber,
                                    })}
                                </Statistic.Group>
                            </Form.Field>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getStatusFeedItem({
                                        label: 'Vehicle Positions',
                                        value: dataState.transitIqData.vehiclesCount,
                                        isValidFunc: isValidNumber,
                                    })}
                                </Statistic.Group>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={16}>
                                <Message attached="top" info>
                                    <p>Coverage, Latency and Vehicle Positions are only for those records which are not later than {dataState.maxTimeDiffMin} minutes.</p>
                                </Message>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getStatusFeedItem({
                                        label: 'Service Alerts',
                                        value: dataState.agencyData.alertsCount,
                                        isValidFunc: isValidNumber,
                                        fileName: 'service-alerts',
                                        downloadFileFunc: getAgencyAlertsFeed,
                                    })}
                                </Statistic.Group>
                            </Form.Field>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getStatusFeedItem({
                                        label: 'Service Alerts',
                                        value: dataState.redisData.alertsCount,
                                        isValidFunc: isValidNumber,
                                    })}
                                </Statistic.Group>
                            </Form.Field>
                            <Form.Field width={6}>
                                <Statistic.Group horizontal size="small">
                                    {getStatusFeedItem({
                                        label: 'Service Alerts',
                                        value: dataState.transitIqData.alertsCount,
                                        isValidFunc: isValidNumber,
                                    })}
                                    {getStatusFeedItem({
                                        label: 'Trip Updates',
                                        value: dataState.transitIqData.tripsCount,
                                        isValidFunc: isValidNumber,
                                    })}
                                </Statistic.Group>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group className="bottomGroup">
                            <Form.Field width={16}>
                                <Message attached="top" info>
                                    <p>Trip Updates also contain arrivals predictions for upcoming trips.</p>
                                </Message>
                            </Form.Field>
                        </Form.Group>
                    </>
                }
            </Form>
        </BlockUi >
    );
};

export default connect(
    (state: AppState) => ({
        agency: getSelectedOrDefaultAgency(state),
    }),
)(ServiceStatusForm);
